import dayjs from 'dayjs';
import { RC_DURATION, ReebeloConditions } from '@lambda/reebelo';
import { BABY_CATEGORIES } from '@lambda/reebelo/src/commonHelpers';
import {
  ShipmentStatus,
  FulFillmentStatusMap,
  ShipmentLineItem,
  Shipment,
} from '@/lib/smartForm/types';
import settings from '@/settings/index';
import {
  POWER_TOOL_CATEGORIES,
  SPORT_FITNESS_CATEGORIES,
} from '@/settings/configs';

export const REEBELO_STORE_TO_RESET_DEVICE_LINK = (store: string) => {
  const links = {
    'reebelo-nz': 'https://help.reebelo.co.nz/hc/en-nz/articles/14373424925977',
    'reebelo-au':
      'https://help.reebelo.com.au/hc/en-au/articles/14348788148377',
    quista: 'https://help.reebelo.sg/hc/en-sg/articles/14373603202073',
    'reebelo-us': 'https://help.reebelo.com/hc/en-gb/articles/15273603411353',
    'reebelo-uk': 'https://help.reebelo.com/hc/en-gb/articles/15273603411353',
    'reebelo-dev': 'https://help.reebelo.com/hc/en-gb/articles/15273603411353',
  };

  return links[store as keyof typeof links];
};

export const REEBELO_STORE_TO_REMOVE_ACCOUNT_LINK = (store: string) => {
  const links = {
    'reebelo-nz': 'https://help.reebelo.co.nz/hc/en-nz/articles/14373428690073',
    'reebelo-au':
      'https://help.reebelo.com.au/hc/en-au/articles/12556563494425',
    quista: 'https://help.reebelo.sg/hc/en-sg/articles/14373553807641',
    'reebelo-us': 'https://help.reebelo.com/hc/en-gb/articles/15273620955801',
    'reebelo-uk': 'https://help.reebelo.com/hc/en-gb/articles/15273620955801',
    'reebelo-dev': 'https://help.reebelo.com/hc/en-gb/articles/15273620955801',
  };

  return links[store as keyof typeof links];
};

export const DEFAULT_ERROR_MESSAGE = {
  title: 'Oops! Something went wrong...',
  description: '',
};

export const DEFAULT_SUCCESS_MESSAGE = {
  title: 'Thank you for submitting a support ticket.',
  description: 'A representative will be reaching out to you shortly.',
};

export const REEBELO_STORE_TO_IMEI_LINK = (store: string) => {
  const links = {
    'reebelo-nz':
      'https://help.reebelo.co.nz/hc/en-nz/articles/14558447201305-How-do-i-find-my-IMEI-number-',
    'reebelo-au':
      'https://help.reebelo.com.au/hc/en-au/articles/12556353458329-How-do-i-find-my-IMEI-number-',
    quista:
      'https://help.reebelo.sg/hc/en-sg/articles/15192073935769-How-do-i-find-my-IMEI-number',
    'reebelo-us':
      'https://help.reebelo.com/hc/en-gb/articles/15273661154841-How-do-i-find-my-IMEI-number-',
    'reebelo-uk':
      'https://help.reebelo.com/hc/en-gb/articles/15273661154841-How-do-i-find-my-IMEI-number-',
    'reebelo-dev':
      'https://help.reebelo.com/hc/en-gb/articles/15273661154841-How-do-i-find-my-IMEI-number-',
  };

  return links[store as keyof typeof links];
};

export const getItemDesc = (lineItem: ShipmentLineItem | undefined) => {
  const name = lineItem?.name;

  return name || '';
};

export const handleExtractFulfillmentStatus = (
  shipment: Shipment | null | undefined,
  deliveryDate: string | null,
  fulfillmentStatus: string,
): ShipmentStatus => {
  const fulfillmentStatusMapping: FulFillmentStatusMap = {
    Delivered: 'delivered',
    OutForDelivery: 'outForDelivery',
    InTransit: 'dispatched',
    Pending: 'dispatched',
    InfoReceived: 'dispatched',
  };

  const shipmentCheckpoints = shipment?.tracking?.checkpoints || [];

  const lastCheckpointShippingStatus =
    shipmentCheckpoints.length > 0
      ? // Order of checkpoints are in asc timestamp - we are interested in latest status
        shipmentCheckpoints?.[shipmentCheckpoints.length - 1]?.tag || null
      : null;

  // We always try to get tracking status from latest checkpoint, otherwise get the aggregated tracking status from aftership (not reliable)
  const aggregatedShippingStatus =
    lastCheckpointShippingStatus ??
    shipment?.tracking?.tag ??
    shipment?.tracking?.status ??
    null;

  if (aggregatedShippingStatus !== null) {
    const status =
      fulfillmentStatusMapping[
        aggregatedShippingStatus as keyof typeof fulfillmentStatusMapping
      ];

    if (status !== null) {
      return status;
    }
  }

  // Assumption: If it is fulfilled we can assume it has been dispatched according to the fulfillmentStatusMapping
  if (fulfillmentStatus === 'FULFILLED') {
    return 'dispatched';
  }

  return 'ordered';
};

export const APAC_DATE_FORMAT = 'DD/MM/YYYY';
export const NA_DATE_FORMAT = 'MM/DD/YYYY';

// We need to change the name of IS_APAC
export const IS_APAC = [
  'reebelo-ca',
  'reebelo-au',
  'reebelo-nz',
  'quista',
].includes(settings.store);
// Update isAPAC in services/lambda/commons/smartforms/helper.ts

export const getWarrantyCoverageTerms = (
  isReebeloCare: boolean,
  lineItem: ShipmentLineItem | undefined,
  duration: RC_DURATION,
) => {
  const isUSStore = ['reebelo-us', 'reebelo-dev'].includes(settings.store);

  if (isReebeloCare) {
    if (isUSStore) {
      return { value: Number(duration), unit: 'month' };
    }

    return { value: 2, unit: 'year' };
  }

  const condition = lineItem?.tags
    ?.find((tag) => tag.startsWith('Condition_'))
    ?.split('_')[1];
  const category = lineItem?.tags
    ?.find((tag) => tag.startsWith('Category_'))
    ?.split('_')[1];

  if (category) {
    const isPowerToolCategory = POWER_TOOL_CATEGORIES.includes(category);
    const isSportFitnessCategory = SPORT_FITNESS_CATEGORIES.includes(category);
    const isBabyCategory = BABY_CATEGORIES.includes(category);

    if (isBabyCategory || isPowerToolCategory || isSportFitnessCategory) {
      return { value: 3, unit: 'month' };
    }
  }

  const isNotBrandNew =
    settings.store === 'quista' && condition !== ReebeloConditions.BrandNew;

  if (isNotBrandNew) {
    return { value: 3, unit: 'month' };
  }

  return { value: 1, unit: 'year' };
};

export const getWarrantyCoverageVersion = (
  isReebeloCare: boolean,
  purchaseDate: string,
  reebelocareVendor: string | null,
) => {
  if (!IS_APAC || !isReebeloCare || !reebelocareVendor || !purchaseDate) {
    return null;
  }

  if (
    reebelocareVendor === 'KTC Warranty' ||
    reebelocareVendor === 'Sonic Connections'
  ) {
    return '(V1)';
  }

  if (reebelocareVendor === 'Cover Genius') {
    const comparisonDate = dayjs('2023-10-01');

    if (dayjs(purchaseDate).isBefore(comparisonDate, 'month')) {
      return '(V2)';
    }

    return '(V3)';
  }

  if (reebelocareVendor === 'SquareTrade' && settings.store === 'reebelo-au') {
    return '(V4)';
  }

  if (
    reebelocareVendor === 'Renewable Care' &&
    settings.store === 'reebelo-au'
  ) {
    const comparisonDate = dayjs('2026-02-16');

    if (dayjs(purchaseDate).isAfter(comparisonDate, 'date')) {
      return '(V5)';
    }

    return '(V1)';
  }

  if (
    reebelocareVendor === 'Renewable Mobile NZ' &&
    settings.store === 'reebelo-nz' &&
    dayjs(purchaseDate).isAfter(dayjs('2026-04-01'), 'date')
  ) {
    return '(V5)';
  }

  return null;
};

export const getSqtContactInfo = () => {
  switch (settings.store) {
    case 'reebelo-au':
      return {
        sqtNumber: '1800 430 835',
        sqtTimezone: 'AEDT/AEST',
        sqtLink: 'https://www.squaretrade.com.au/service-request/',
      };
    case 'reebelo-ca':
    case 'reebelo-dev':
      return {
        sqtNumber: '877-909-9780',
        sqtTimezone: 'PST',
        sqtLink: 'https://www.squaretrade.ca/',
      };
    default:
      return {
        sqtNumber: '877-909-9780',
        sqtTimezone: 'PST',
        sqtLink: 'https://www.squaretrade.ca/',
      };
  }
};
